import { useCombobox } from 'downshift';
import { forwardRef } from 'react';

import { FormField, InputBar } from '@xing-com/text-field';
import type { FormFieldProps, InputBarProps } from '@xing-com/text-field';

import * as Styled from './auto-complete.styles';

type OmitTextFieldProps<T> = Omit<T, 'onChange' | 'onSelect' | 'variant'>;

type FormFieldVariant = {
  variant?: 'formField';
  textFieldVariant?: FormFieldProps['variant'];
} & OmitTextFieldProps<FormFieldProps>;

type InputBarVariant = {
  variant: 'inputBar';
  textFieldVariant?: InputBarProps['variant'];
} & OmitTextFieldProps<InputBarProps>;

type CommonProps<T> = {
  suggestions?: T[];
  inputClassName?: string;
  menuLabel?: string;
  onChange?: (inputValue: string) => void;
  onSelect?: (selectedItem: T) => void;
  itemRenderer?: (item: T) => JSX.Element | string;
  isItemDisabled?: (item: T, index: number) => boolean;
} & (T extends string
  ? { suggestionToString?: (item: T | null) => string }
  : { suggestionToString: (item: T | null) => string });

export type AutoCompleteProps<T = string> = CommonProps<T> &
  (FormFieldVariant | InputBarVariant);

const AutoCompleteBase = <T,>(
  {
    className,
    id,
    innerRef,
    inputClassName,
    itemRenderer,
    isItemDisabled = () => false,
    onChange,
    onKeyDown,
    onSelect,
    suggestionToString,
    suggestions,
    textFieldVariant,
    value,
    variant,
    menuLabel,
    ...inputRestProps
  }: AutoCompleteProps<T>,
  forwardedRef: React.ForwardedRef<HTMLInputElement | HTMLTextAreaElement>
): React.ReactElement => {
  const {
    isOpen,
    inputValue,
    highlightedIndex,
    getInputProps,
    getMenuProps,
    getItemProps,
  } = useCombobox<T>({
    id,
    items: suggestions ?? [],
    inputValue: value,
    ...(suggestionToString && { itemToString: suggestionToString }),
    stateReducer: (_, { type, changes }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
        case useCombobox.stateChangeTypes.InputClick:
          // We need to remove the selected item from the downshift internal state,
          // so we can select the same item consecutively (onSelectedItemChange won't be called otherwise)
          return { ...changes, selectedItem: undefined };
        default:
          return changes;
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (onSelect && selectedItem) onSelect(selectedItem);
    },
    isItemDisabled,
  });

  const defaultItemRenderer = (
    suggestionValue: string
  ): JSX.Element | string => {
    const position = inputValue
      ? suggestionValue.toLowerCase().indexOf(inputValue.toLowerCase())
      : -1;

    if (position >= 0) {
      const left = suggestionValue.substring(0, position);
      const middle = suggestionValue.substring(
        position,
        position + inputValue.length
      );
      const right = suggestionValue.substring(position + inputValue.length);

      return (
        <>
          {left}
          <strong>{middle}</strong>
          {right}
        </>
      );
    }

    return suggestionValue;
  };

  const InputComponent = variant === 'inputBar' ? InputBar : FormField;
  const ref = innerRef || forwardedRef;

  return (
    <Styled.AutoComplete className={className}>
      {/* @ts-expect-error FIXME: probably wild mix with getInputProps, text-fields and multiline props */}
      <InputComponent
        // @ts-expect-error FIXME:  probably wild mix with getInputProps, text-fields and multiline props
        {...getInputProps({
          ref,
          className: inputClassName,
          variant: textFieldVariant,
          // Workaround to fix the cursor jump
          // See: https://github.com/downshift-js/downshift/tree/59d3498faafd3c3b4832a20dab7485502ce67284/src/hooks/useCombobox#inputvalue
          onChange: (
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
          ): void => {
            onChange?.(event.target.value);
          },
          // Prevent Downshift's default 'Enter' behavior, if no element is highlighted we want to submit the form
          // See: https://github.com/downshift-js/downshift/tree/59d3498faafd3c3b4832a20dab7485502ce67284/src/hooks/useCombobox#customizing-handlers
          onKeyDown: (
            event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
          ): void => {
            if (event.key === 'Enter' && highlightedIndex === -1) {
              // @ts-expect-error preventDownshiftDefault is a custom property
              event.nativeEvent.preventDownshiftDefault = true;
            }
            // @ts-expect-error onKeyDown never type
            onKeyDown?.(event);
          },
          // the labeling is handled by the text field component
          'aria-labelledby': undefined,
          ...inputRestProps,
        })}
      />
      <Styled.Menu
        {...getMenuProps({
          'aria-labelledby': undefined,
          'aria-label': menuLabel,
        })}
      >
        {isOpen &&
          suggestions?.map((item, index) => {
            const suggestionValue = suggestionToString
              ? suggestionToString(item)
              : // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                (item as string);

            return (
              <Styled.MenuItem
                key={`${suggestionValue}-${index}`}
                $isHighlighted={highlightedIndex === index}
                {...getItemProps({ item, index })}
              >
                {itemRenderer
                  ? itemRenderer(item)
                  : defaultItemRenderer(suggestionValue)}
              </Styled.MenuItem>
            );
          })}
      </Styled.Menu>
    </Styled.AutoComplete>
  );
};

// https://www.totaltypescript.com/forwardref-with-generic-components
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
function fixedForwardRef<T, P = {}>(
  render: (props: P, ref: React.Ref<T>) => React.ReactNode
): (props: P & React.RefAttributes<T>) => React.ReactNode {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
  return forwardRef(render) as any;
}

export const AutoComplete = fixedForwardRef(AutoCompleteBase);
